import { useState, useCallback } from 'react';

// Debug the import first
console.log('Attempting to import @supabase/supabase-js...');
let supabase = null;
let createClient = null;

try {
  const supabaseModule = require('@supabase/supabase-js');
  createClient = supabaseModule.createClient;
  console.log('Supabase module imported successfully:', supabaseModule);
  console.log('createClient function:', createClient);
  
  if (createClient) {
    const supabaseUrl = 'https://xwhnqxynhcvrciaucypv.supabase.co';
    const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh3aG5xeHluaGN2cmNpYXVjeXB2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQxNjg4NjcsImV4cCI6MjA2OTc0NDg2N30.GASvJDKDhvAz9IoW04jC5DsRYKBlQlLgRhLN4AOLrn4';
    supabase = createClient(supabaseUrl, supabaseAnonKey);
    console.log('Supabase client created:', supabase);
    console.log('Supabase client from method:', supabase?.from ? 'EXISTS' : 'MISSING');
  }
} catch (error) {
  console.error('Error importing Supabase:', error);
}

const useSupabase = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simple distance calculation (placeholder)
  const calculateDistance = (location) => {
    if (!location) return 0;
    if (location.includes('Austin')) return Math.random() * 10;
    if (location.includes('San Antonio')) return 78 + Math.random() * 10;
    if (location.includes('Houston')) return 165 + Math.random() * 10;
    if (location.includes('Dallas')) return 195 + Math.random() * 10;
    return Math.random() * 200;
  };

  const PROP_SELECT_FIELDS = [
    'id',
    'name',
    'category_id',
    'usage_category_id',
    'owner_org_id',
    'location',
    'condition',
    'material',
    'tags',
    'colors',
    'size',
    'usage',
    'use_how',
    'use_when',
    'use_where',
    'historical_period',
    'historical_region',
    'verified',
    'retention_agreement',
    'modification_allowed',
    'transfer_available',
    'is_consumable',
    'consumable_policy',
    'private_notes',
    'barcode',
    'purchase_date',
    'main_image_src',
    'status',
    'public_visible',
    'images',
    'reservation_id',
    'mask_svg'
  ].join(',');

  const normalizeArrayField = (value) => {
    if (Array.isArray(value)) {
      return value.map((entry) => (typeof entry === 'string' ? entry.trim() : entry)).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(entry => entry.trim())
        .filter(Boolean);
    }
    return [];
  };

  const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    return false;
  };

  // Fetch all initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch organizations
      const { data: orgsData, error: orgsError } = await supabase
        .from('organizations')
        .select('*');
      
      if (orgsError) throw orgsError;

      // Fetch props with organization info and category name
      const { data: propsData, error: propsError } = await supabase
        .from('props')
        .select(`
          ${PROP_SELECT_FIELDS},
          owner_org:organizations(name, location),
          category_info:categories!props_category_id_fkey(id, name),
          usage_category:categories!props_usage_category_id_fkey(id, name)
        `)
        .eq('public_visible', true);

      if (propsError) throw propsError;

      // Transform the data to match our component's expected format
      const transformedProps = (propsData || []).map(prop => {
        const convertedColors = normalizeArrayField(prop.colors);
        const convertedMaterials = normalizeArrayField(prop.material);
        const normalizedStatus = (prop.status || '').toLowerCase();
        
        console.log(`🔍 Loading prop ${prop.name}, images:`, prop.images);
        const processedImages = Array.isArray(prop.images)
          ? prop.images.map(image => (typeof image === 'string' ? image : image?.src || image?.url)).filter(Boolean)
          : [];
        
        return {
          ...prop,
          slug: prop.slug || (prop.name ? prop.name.toLowerCase().replace(/\s+/g, '-') : prop.id),
          owner: prop.owner_org?.name || 'Unknown',
          location: prop.owner_org?.location || prop.location,
          distance: calculateDistance(prop.owner_org?.location || prop.location),
          inCart: false,
          images: processedImages,
          tags: normalizeArrayField(prop.tags),
          colors: convertedColors,
          dominant_color: convertedColors.length ? convertedColors[0] : null,
          material: convertedMaterials,
          size: prop.size || '',
          usage: prop.usage || '',
          usage_category_id: prop.usage_category_id || '',
          usage_category_name: prop.usage_category?.name || '',
          main_image_src: prop.main_image_src || null,
          use_how: prop.use_how || '',
          use_when: prop.use_when || '',
          use_where: prop.use_where || '',
          is_consumable: normalizeBoolean(prop.is_consumable),
          modification_allowed: normalizeBoolean(prop.modification_allowed),
          transfer_available: normalizeBoolean(prop.transfer_available),
          // Keep the actual prop status from database - don't override based on reservation_id
          status: normalizedStatus || prop.status,
          // Use resolved category name from categories table, fallback to 'Uncategorized'
          category: prop.category_info?.name || 'Uncategorized',
          _imageTimestamp: Date.now() // Add timestamp for cache busting on local images
        };
      });

      // Fetch transfer requests
      const { data: transfersData, error: transfersError } = await supabase
        .from('transfer_requests')
        .select(`
          *,
          prop:props(name, category_info:categories!props_category_id_fkey(name)),
          from_org:organizations!transfer_requests_from_org_id_fkey(name),
          to_org:organizations!transfer_requests_to_org_id_fkey(name),
          requester:profiles(full_name)
        `);

      if (transfersError) throw transfersError;

      console.log('Data loaded successfully:', {
        props: transformedProps.length,
        organizations: orgsData?.length || 0,
        transfers: transfersData?.length || 0
      });

      return {
        props: transformedProps,
        organizations: orgsData || [],
        transferRequests: transfersData || []
      };

    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch user lists
  const fetchUserLists = async (currentUser) => {
    try {
      console.log('Fetching user lists for:', currentUser);
      
      // Use direct SQL query to bypass client issues
      const { data: listsData, error: listsError } = await supabase
        .from('user_lists')
        .select('*')
        .eq('user_id', currentUser.name)
        .order('created_at', { ascending: false });
      
      if (listsError) {
        console.error('Direct query error:', listsError);
        return []; // Return empty array instead of crashing
      }

      console.log('Raw lists data:', listsData);

      // Get list items for each list
      const transformedLists = [];
      for (const list of listsData || []) {
        const { data: items } = await supabase
          .from('list_items')
          .select('prop_id')
          .eq('list_id', list.id);
        
        transformedLists.push({
          id: list.id,
          name: list.name,
          description: list.description,
          propIds: items ? items.map(item => item.prop_id) : [],
          createdAt: new Date(list.created_at),
          updatedAt: new Date(list.updated_at)
        });
      }

      console.log('Transformed user lists:', transformedLists);
      return transformedLists;

    } catch (err) {
      console.error('Error fetching user lists:', err);
      return []; // Return empty array instead of crashing
    }
  };

  // Create a new user list
  const createUserList = async (listData, currentUser) => {
    try {
      console.log('Creating user list with data:', listData, 'for user:', currentUser);

      // Handle different parameter formats
      const listName = listData.name || listData.listName || listData;
      const listDescription = listData.description || listData.listDescription || '';
      
      if (!listName || typeof listName !== 'string') {
        throw new Error('List name is required');
      }

      const insertData = {
        name: listName,
        description: listDescription,
        user_id: currentUser.name
      };
      
      console.log('Inserting list data:', insertData);

      const { data, error } = await supabase
        .from('user_lists')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        propIds: [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };

    } catch (err) {
      console.error('Error creating user list:', err);
      throw err;
    }
  };

  const submitFeedback = async (userProfile, formData) => {
    try {
      const payload = {
        user_id: userProfile?.id || null,
        user_name: userProfile?.name || null,
        category: formData.category || 'general',
        rating: formData.rating || 0,
        message: formData.message,
        contact_email: formData.email || userProfile?.email || null,
        meta: {
          role: userProfile?.role || null,
          organization: userProfile?.organization || null
        }
      };

      const { error } = await supabase
        .from('feedback')
        .insert(payload);

      if (error) {
        console.error('Feedback submission failed:', error);
        throw error;
      }

      return true;
    } catch (err) {
      console.error('submitFeedback error:', err);
      throw err;
    }
  };

  // Add prop to list
  const addPropToList = async (propId, listId) => {
    try {
      const { error } = await supabase
        .from('list_items')
        .insert([{
          list_id: listId,
          prop_id: propId
        }]);

      if (error) throw error;
      return true;

    } catch (err) {
      console.error('Error adding prop to list:', err);
      throw err;
    }
  };

  // Remove prop from list
  const removePropFromList = async (propId, listId) => {
    try {
      const { error } = await supabase
        .from('list_items')
        .delete()
        .eq('list_id', listId)
        .eq('prop_id', propId);

      if (error) throw error;
      return true;

    } catch (err) {
      console.error('Error removing prop from list:', err);
      throw err;
    }
  };

  // Delete user list
  const deleteUserList = async (listId) => {
    try {
      const { error } = await supabase
        .from('user_lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;
      return true;

    } catch (err) {
      console.error('Error deleting user list:', err);
      throw err;
    }
  };

  // Submit a reservation request (fixed target_org_id handling)
  const submitReservationRequest = async (propIds, currentUser, expectedReturnDate, purpose, notes = "", options = {}) => {
    try {
      console.log('Starting reservation submission with props:', propIds);
      
      let requestingOrgId = null;
      if (currentUser?.organization) {
        const { data: requestedOrg, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .eq('name', currentUser.organization)
          .single();
        
        if (orgError) {
          console.warn('Unable to resolve requesting organization:', orgError);
        } else if (requestedOrg?.id) {
          requestingOrgId = requestedOrg.id;
        }
      }

      console.log('Resolved requesting org ID:', requestingOrgId);

      // Group props by their owning organization
      const propsByOrg = {};
      for (const propId of propIds) {
        console.log('Processing prop ID:', propId);
        
        const { data: prop, error: propError } = await supabase
          .from('props')
          .select('owner_org_id, name')
          .eq('id', propId)
          .single();
        
        if (propError) {
          console.error('Error fetching prop:', propError);
          throw propError;
        }
        
        if (prop && prop.owner_org_id) {
          console.log('Found prop:', prop, 'Owner org ID:', prop.owner_org_id);
          if (!propsByOrg[prop.owner_org_id]) {
            propsByOrg[prop.owner_org_id] = [];
          }
          propsByOrg[prop.owner_org_id].push(propId);
        } else {
          console.error('Prop has no owner_org_id:', prop);
          throw new Error(`Prop ${propId} has no owner organization`);
        }
      }

      console.log('Props grouped by org:', propsByOrg);

      // Create separate reservations for each target organization
      const createdReservations = [];
      for (const [targetOrgId, orgPropIds] of Object.entries(propsByOrg)) {
        console.log('Creating reservation for target org:', targetOrgId, 'with props:', orgPropIds);
        
        // Make sure targetOrgId is not null and is a valid UUID
        if (!targetOrgId || targetOrgId === 'null') {
          throw new Error('Invalid target organization ID');
        }
        
        // Create reservation
        const reservationData = {
          user_id: currentUser?.id || null,
          target_org_id: targetOrgId, // Keep as UUID string - this was the issue!
        requested_by_name: currentUser?.name || 'Propshare Guest',
          purpose: purpose || 'General Use',
          expected_return_date: expectedReturnDate,
          notes: notes,
          status: 'pending'
        };

        if (requestingOrgId) {
          reservationData.requesting_org_id = requestingOrgId;
        }

        // Add optional pickup preferences
        if (options.preferredPickupDay) {
          reservationData.preferred_pickup_day = options.preferredPickupDay;
        }
        if (options.preferredTimeRange) {
          reservationData.preferred_time_range = options.preferredTimeRange;
        }

        console.log('Final reservation data being inserted:', reservationData);
        console.log('Target org ID type:', typeof reservationData.target_org_id, 'Value:', reservationData.target_org_id);

        const { data: reservation, error: reservationError } = await supabase
          .from('reservations')
          .insert([reservationData])
          .select()
          .single();

        if (reservationError) {
          console.error('Error creating reservation:', reservationError);
          throw reservationError;
        }

        console.log('Created reservation:', reservation);

        // Create reservation items
        const reservationItems = orgPropIds.map(propId => ({
          reservation_id: reservation.id,
          prop_id: propId // Keep as UUID string, don't parse as integer
        }));

        console.log('Creating reservation items:', reservationItems);

        const { error: itemsError } = await supabase
          .from('reservation_items')
          .insert(reservationItems);

        if (itemsError) {
          console.error('Error creating reservation items:', itemsError);
          throw itemsError;
        }

        // Update props to show they're reserved
        console.log('Updating props to reserved status:', orgPropIds);
        
        const { error: propsError } = await supabase
          .from('props')
          .update({ 
            status: 'reserved',
            reservation_id: reservation.id
          })
          .in('id', orgPropIds); // Keep as UUID strings

        if (propsError) {
          console.error('Error updating props status:', propsError);
          throw propsError;
        }

        createdReservations.push({
          reservation,
          propIds: orgPropIds
        });
      }

      console.log('All reservations created successfully:', createdReservations);
      return createdReservations;

    } catch (err) {
      console.error('Error submitting reservation request:', err);
      throw err;
    }
  };

  // Fetch reservations for current user/organization
  const fetchReservations = async (currentUser, status = null) => {
    try {
      console.log('fetchReservations called with:', { currentUser, status });
      
    let query = supabase
      .from('reservations')
      .select(`
        *,
        requesting_org:organizations!reservations_requesting_org_id_fkey(name, location),
        target_org:organizations!reservations_target_org_id_fkey(name, location),
        reservation_items(
          prop_id,
          props(id, name, status, category_info:categories!props_category_id_fkey(name), images)
        )
      `);

    const userId = currentUser?.id;
    const userOrgName = currentUser?.organization;
    const userRole = currentUser?.role;

    if (userId && userRole !== 'warehouse_admin') {
      console.log('Fetching reservations by user_id:', userId);
      query = query.eq('user_id', userId);
    } else if (userOrgName) {
      // Get user's organization record (fall back for admins and service users)
      const userOrg = await supabase
        .from('organizations')
        .select('id')
        .eq('name', userOrgName)
        .single();

      console.log('User organization record:', userOrg.data);

      if (userOrg.data) {
        if (userRole === 'warehouse_admin') {
          console.log('Fetching reservations for warehouse admin, target_org_id:', userOrg.data.id);
          query = query.eq('target_org_id', userOrg.data.id);
        } else {
          console.log('Fetching reservations for organization member, requesting_org_id:', userOrg.data.id);
          query = query.eq('requesting_org_id', userOrg.data.id);
        }
      }
    }

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error in query:', error);
        throw error;
      }

      console.log('Reservations fetched:', data);
      return data || [];

    } catch (err) {
      console.error('Error fetching reservations:', err);
      throw err;
    }
  };

  // Approve/reject reservation
  const updateReservationStatus = async (reservationId, status, approvalData = {}) => {
    try {
      const updateData = { 
        status,
        ...approvalData
      };

      if (status === 'approved') {
        updateData.approved_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', reservationId)
        .select()
        .single();

      if (error) throw error;

      // Update prop statuses based on reservation status
      if (status === 'rejected' || status === 'cancelled') {
        // Free up the props
        const { error: propsError } = await supabase
          .from('props')
          .update({ 
            status: 'available',
            reservation_id: null
          })
          .eq('reservation_id', reservationId);

        if (propsError) {
          console.warn('Failed to free up props:', propsError);
        }
      }
      // Note: When approved, props should stay 'reserved' (no change needed)

      return data;

    } catch (err) {
      console.error('Error updating reservation status:', err);
      throw err;
    }
  };

  // Complete reservation (when props are actually checked out)
  const completeReservation = async (reservationId, currentUser) => {
    try {
      // Update reservation to active_checkout (not completed yet)
      const { data: reservation, error: reservationError } = await supabase
        .from('reservations')
        .update({ 
          status: 'active_checkout'
        })
        .eq('id', reservationId)
        .select('*, reservation_items(*)')
        .single();

      if (reservationError) throw reservationError;

      // Get the props in this reservation
      const propIds = reservation.reservation_items.map(item => item.prop_id);

      // Update props to checked_out status
      const { error: propsError } = await supabase
        .from('props')
        .update({ 
          status: 'checked_out',
          return_date: reservation.expected_return_date,
          reservation_id: reservationId // Keep the reservation link
        })
        .in('id', propIds);

      if (propsError) throw propsError;

      // Create checkout history records
      const userOrg = await supabase
        .from('organizations')
        .select('id')
        .eq('name', currentUser.organization)
        .single();

      if (userOrg.data) {
        const checkoutRecords = propIds.map(propId => ({
          prop_id: propId,
          organization_id: userOrg.data.id,
          expected_return_date: reservation.expected_return_date,
          purpose: reservation.purpose || 'General Use',
          notes: `Completed via reservation #${reservationId}`
        }));

        const { error: historyError } = await supabase
          .from('checkout_history')
          .insert(checkoutRecords);

        if (historyError) {
          console.warn('Failed to create checkout history:', historyError);
        }

        // Increment checkout count for each prop
        for (const propId of propIds) {
          const { error: countError } = await supabase.rpc('increment_checkout_count', { prop_id: propId });
          if (countError) {
            console.warn('Failed to increment checkout count:', countError);
          }
        }
      }

      return reservation;

    } catch (err) {
      console.error('Error completing reservation:', err);
      throw err;
    }
  };

  // Request a prop transfer
  const requestTransfer = async (propId, notes = "", currentUser, organizations, props) => {
    try {
      const prop = props.find(p => p.id === propId);
      const userOrg = organizations.find(org => org.name === currentUser.organization);
      
      if (!prop || !userOrg) {
        throw new Error('Could not find prop or organization');
      }

      console.log('Transfer request data:', {
        prop_id: propId,
        from_org_id: prop.owner_org_id,
        to_org_id: userOrg.id,
        notes: notes
      });

      const { data, error } = await supabase
        .from('transfer_requests')
        .insert([{
          prop_id: propId,
          from_org_id: prop.owner_org_id,
          to_org_id: userOrg.id,
          notes: notes,
          estimated_arrival: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        }])
        .select();

      if (error) throw error;

      return data;

    } catch (err) {
      console.error('Error requesting transfer:', err);
      throw err;
    }
  };

  // Save a prop (create or update)
  const saveProp = async (propForm, editingProp, currentUser, organizations) => {
    try {
      const userOrg = organizations.find(org => org.name === currentUser.organization);
      if (!userOrg) {
        throw new Error('Could not find your organization');
      }

      // Prepare the data
      const propData = {
        name: propForm.name,
        category_id: propForm.category_id, // Use category_id instead of category
        owner_org_id: userOrg.id,
        length: propForm.length,
        width: propForm.width,
        height: propForm.height,
        weight: propForm.weight,
        condition: propForm.condition,
        material: propForm.material,
        tags: propForm.tags,
        colors: propForm.colors || [],
        historical_period: propForm.historical_period,
        historical_region: propForm.historical_region,
        verified: propForm.verified,
        retention_agreement: propForm.retention_agreement,
        modification_allowed: propForm.modification_allowed,
        transfer_available: propForm.transfer_available,
        is_consumable: propForm.is_consumable,
        consumable_policy: propForm.consumable_policy,
        size: propForm.size || '',
        usage_category_id: propForm.usage_category_id || null,
        use_how: propForm.use_how,
        use_when: propForm.use_when,
        use_where: propForm.use_where,
        private_notes: propForm.private_notes,
        barcode: propForm.barcode,
        purchase_date: propForm.purchase_date || null,
        public_visible: true,
        status: propForm.status,
        images: propForm.images || []
      };

      console.log('💾 Saving prop with images:', propData.images);
      console.log('🔍 Full prop data:', propData);

      let result;
      if (editingProp) {
        // Update existing prop
        result = await supabase
          .from('props')
          .update(propData)
          .eq('id', editingProp.id)
          .select();
      } else {
        // Create new prop
        result = await supabase
          .from('props')
          .insert([propData])
          .select();
      }

      if (result.error) throw result.error;

      console.log('✅ Database save result:', result.data);
      console.log('📋 Images in saved data:', result.data?.[0]?.images);

      return result.data;

    } catch (err) {
      console.error('Error saving prop:', err);
      throw err;
    }
  };

  // Delete a prop
  const deleteProp = async (propId) => {
    try {
      const { error } = await supabase
        .from('props')
        .delete()
        .eq('id', propId);

      if (error) throw error;

      return true;

    } catch (err) {
      console.error('Error deleting prop:', err);
      throw err;
    }
  };

  // Check out props (change status to checked_out)
  const checkoutProps = async (propIds, currentUser, expectedReturnDate, purpose) => {
    try {
      const userOrg = await supabase
        .from('organizations')
        .select('id')
        .eq('name', currentUser.organization)
        .single();

      if (!userOrg.data) {
        throw new Error('Organization not found');
      }

      // Create a reservation record for this direct checkout
      const { data: reservation, error: reservationError } = await supabase
        .from('reservations')
        .insert([{
          requesting_org_id: userOrg.data.id,
          target_org_id: userOrg.data.id, // Same org for direct checkout
          requested_by_name: currentUser.name,
          purpose: purpose || 'Direct Checkout',
          expected_return_date: expectedReturnDate,
          status: 'active_checkout',
          notes: 'Direct checkout from inventory'
        }])
        .select()
        .single();

      if (reservationError) throw reservationError;

      // Create reservation items
      const reservationItems = propIds.map(propId => ({
        reservation_id: reservation.id,
        prop_id: propId
      }));

      const { error: itemsError } = await supabase
        .from('reservation_items')
        .insert(reservationItems);

      if (itemsError) throw itemsError;

      // Update prop status to checked_out
      const { data, error } = await supabase
        .from('props')
        .update({ 
          status: 'checked_out',
          return_date: expectedReturnDate,
          reservation_id: reservation.id
        })
        .in('id', propIds)
        .select();

      if (error) throw error;

      // Create checkout history records
      const checkoutRecords = propIds.map(propId => ({
        prop_id: propId,
        organization_id: userOrg.data.id,
        expected_return_date: expectedReturnDate,
        purpose: purpose || 'Direct Checkout',
        notes: `Direct checkout via PropShare Kiosk - Reservation #${reservation.id}`
      }));

      const { error: historyError } = await supabase
        .from('checkout_history')
        .insert(checkoutRecords);

      if (historyError) {
        console.warn('Failed to create checkout history:', historyError);
        // Don't throw here - prop checkout still succeeded
      }

      // Increment checkout count
      for (const propId of propIds) {
        const { error: countError } = await supabase.rpc('increment_checkout_count', { prop_id: propId });
        if (countError) {
          console.warn('Failed to increment checkout count:', countError);
        }
      }

      return data;

    } catch (err) {
      console.error('Error checking out props:', err);
      throw err;
    }
  };

  // Return props (change status back to available)
  const returnProps = async (propIds) => {
    try {
      const { data, error } = await supabase
        .from('props')
        .update({ 
          status: 'available',
          return_date: null
        })
        .in('id', propIds)
        .select();

      if (error) throw error;

      // Update checkout history with actual return date
      const { error: historyError } = await supabase
        .from('checkout_history')
        .update({ 
          actual_return_date: new Date().toISOString().split('T')[0]
        })
        .in('prop_id', propIds)
        .is('actual_return_date', null);

      if (historyError) {
        console.warn('Failed to update checkout history:', historyError);
      }

      // Check if any active reservations should be marked as completed
      await checkAndCompleteReservations(propIds);

      return data;

    } catch (err) {
      console.error('Error returning props:', err);
      throw err;
    }
  };

  // Check if reservations should be completed when props are returned
  const checkAndCompleteReservations = async (returnedPropIds) => {
    try {
      console.log('🔍 Checking for reservations to complete after returning props:', returnedPropIds);
      
      // Find active_checkout reservations that include these props
      const { data: reservations, error } = await supabase
        .from('reservations')
        .select(`
          id,
          reservation_items(prop_id)
        `)
        .eq('status', 'active_checkout');

      if (error) throw error;
      
      console.log('📋 Found active_checkout reservations:', reservations?.length || 0);

      for (const reservation of reservations || []) {
        const reservationPropIds = reservation.reservation_items.map(item => item.prop_id);
        console.log(`📦 Reservation ${reservation.id} has props:`, reservationPropIds);
        
        // Check if any props from this reservation were returned
        const hasReturnedProps = returnedPropIds.some(id => reservationPropIds.includes(id));
        console.log(`🔄 Has returned props from this reservation:`, hasReturnedProps);
        
        if (hasReturnedProps) {
          // Check if ALL props in this reservation are now available
          const { data: propStatuses } = await supabase
            .from('props')
            .select('id, status')
            .in('id', reservationPropIds);
          
          console.log(`📊 Current prop statuses for reservation ${reservation.id}:`, propStatuses);
          
          const allReturned = propStatuses?.every(prop => prop.status === 'available');
          console.log(`✅ All props returned:`, allReturned);
          
          if (allReturned) {
            // Mark reservation as completed
            console.log(`🎉 Marking reservation ${reservation.id} as completed`);
            const { data: updateResult, error: updateError } = await supabase
              .from('reservations')
              .update({ status: 'completed' })
              .eq('id', reservation.id)
              .select();
            
            if (updateError) {
              console.error('❌ Error updating reservation:', updateError);
            } else {
              console.log('✅ Successfully marked reservation as completed:', updateResult);
            }
          }
        }
      }
    } catch (err) {
      console.error('❌ Error checking reservation completion:', err);
    }
  };

  // ============ USER MANAGEMENT FUNCTIONS ============

  // Fetch users for a specific organization
  const fetchOrganizationUsers = async (organizationId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          role,
          location,
          created_at
        `)
        .eq('organization_id', String(organizationId))
        .order('full_name');
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching organization users:', err);
      throw err;
    }
  };

  // Add user to organization
  const addUserToOrganization = async (userData, organizationId) => {
    try {
      const userProfile = {
        full_name: userData.full_name,
        role: userData.role || 'general',
        organization_id: organizationId,
        location: userData.location || '',
        // Note: In production, this would be linked to auth.users via user_id
        // For now we're using mock data structure
      };

      const { data, error } = await supabase
        .from('profiles')
        .insert([userProfile])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error adding user to organization:', err);
      throw err;
    }
  };

  // Update user profile
  const updateUser = async (userId, userData) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          full_name: userData.full_name,
          role: userData.role,
          location: userData.location
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  // Remove user from organization (soft delete by setting organization_id to null)
  const removeUserFromOrganization = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ organization_id: null })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error removing user from organization:', err);
      throw err;
    }
  };

  // Transfer user to different organization
  const transferUserToOrganization = async (userId, newOrganizationId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ organization_id: newOrganizationId })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error transferring user:', err);
      throw err;
    }
  };

  // ============ CATEGORY MANAGEMENT FUNCTIONS ============

  // Fetch all categories with hierarchical structure
  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('level')
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching categories:', err);
      throw err;
    }
  }, []);

  // Fetch categories in tree structure for display
  const fetchCategoryTree = async () => {
    try {
      const categories = await fetchCategories();
      
      // Build tree structure
      const rootCategories = categories.filter(cat => !cat.parent_id);
      const buildTree = (parentId) => {
        return categories
          .filter(cat => cat.parent_id === parentId)
          .map(cat => ({
            ...cat,
            children: buildTree(cat.id)
          }));
      };
      
      return rootCategories.map(cat => ({
        ...cat,
        children: buildTree(cat.id)
      }));
    } catch (err) {
      console.error('Error building category tree:', err);
      throw err;
    }
  };

  // Create new category
  const createCategory = async (categoryData) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([categoryData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating category:', err);
      throw err;
    }
  };

  // Update category
  const updateCategory = async (categoryId, categoryData) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update(categoryData)
        .eq('id', categoryId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating category:', err);
      throw err;
    }
  };

  // Delete category (and optionally move props to new category)
  const deleteCategory = async (categoryId, movePropsTo = null) => {
    try {
      // If moving props, update them first
      if (movePropsTo) {
        const { error: propsError } = await supabase
          .from('props')
          .update({ category_id: movePropsTo })
          .eq('category_id', categoryId);
        
        if (propsError) throw propsError;
      }
      
      // Delete the category
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);
      
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error deleting category:', err);
      throw err;
    }
  };

  // Get category usage statistics
  const getCategoryStats = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id,
          name,
          level,
          materialized_path,
          props(count)
        `);
      
      if (error) throw error;
      
      return (data || []).map(cat => ({
        ...cat,
        propCount: cat.props?.[0]?.count || 0
      }));
    } catch (err) {
      console.error('Error fetching category stats:', err);
      throw err;
    }
  };

  // Get hierarchical category usage statistics (includes subcategories)
  const getHierarchicalCategoryStats = async () => {
    try {
      // Get all categories
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select(`
          id,
          name,
          level,
          parent_id,
          materialized_path
        `);
      
      if (categoriesError) throw categoriesError;
      
      // Get all props with their category names (using the same logic as search)
      const { data: props, error: propsError } = await supabase
        .from('props')
        .select(`
          id,
          category_id,
          category_info:categories!props_category_id_fkey(name)
        `)
        .eq('public_visible', true);
      
      if (propsError) throw propsError;
      
      console.log('🔍 Debug - Categories:', categories.length);
      console.log('🔍 Debug - Props:', props.length);
      console.log('🔍 Debug - Sample props:', props.slice(0, 5).map(p => ({
        id: p.id,
        category_id: p.category_id,
        category_name: p.category_info?.name
      })));
      
      // Create a map of category names to prop counts (direct assignments only)
      const directCategoryPropCounts = new Map();
      let uncategorizedCount = 0;
      props.forEach(prop => {
        const categoryName = prop.category_info?.name;
        if (categoryName) {
          directCategoryPropCounts.set(categoryName, (directCategoryPropCounts.get(categoryName) || 0) + 1);
        } else {
          uncategorizedCount++;
        }
      });
      
      console.log('🔍 Debug - Direct category counts:', Array.from(directCategoryPropCounts.entries()));
      console.log('🔍 Debug - Uncategorized props:', uncategorizedCount);
      
      // Calculate hierarchical counts for each category
      // Note: This function is not currently used - direct counts are used to avoid double-counting
      // Kept for reference in case hierarchical aggregation is needed in future
      // eslint-disable-next-line no-unused-vars
      const _calculateHierarchicalCount = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        if (!category) return 0;
        
        // Get direct props for this category
        const directCount = directCategoryPropCounts.get(category.name) || 0;
        
        // Get all subcategories
        const subcategories = categories.filter(cat => cat.parent_id === categoryId);
        
        // Recursively count props in subcategories
        const subcategoryCount = subcategories.reduce((sum, subcat) => {
          return sum + _calculateHierarchicalCount(subcat.id);
        }, 0);
        
        const totalCount = directCount + subcategoryCount;
        
        if (totalCount > 0) {
          console.log(`🔍 Debug - ${category.name}: direct=${directCount}, subcategories=${subcategoryCount}, total=${totalCount}`);
        }
        
        return totalCount;
      };
      
      // Return stats with direct counts only (no hierarchical counting to avoid double-counting)
      const result = categories.map(cat => ({
        ...cat,
        propCount: directCategoryPropCounts.get(cat.name) || 0
      }));
      
      // Add uncategorized category if there are uncategorized props
      if (uncategorizedCount > 0) {
        result.push({
          id: 'uncategorized',
          name: 'Uncategorized',
          level: 0,
          parent_id: null,
          materialized_path: 'Uncategorized',
          propCount: uncategorizedCount
        });
      }
      
      console.log('🔍 Debug - Final hierarchical stats:', result.filter(r => r.propCount > 0));
      console.log('🔍 Debug - Total props accounted for:', result.reduce((sum, cat) => sum + cat.propCount, 0));
      console.log('🔍 Debug - Expected total props:', props.length);
      console.log('🔍 Debug - Uncategorized props:', uncategorizedCount);
      
      return result;
    } catch (err) {
      console.error('Error fetching hierarchical category stats:', err);
      throw err;
    }
  };

  // Search categories by name or path
  const searchCategories = async (query) => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .or(`name.ilike.%${query}%,materialized_path.ilike.%${query}%`)
        .order('level')
        .order('sort_order');
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error searching categories:', err);
      throw err;
    }
  };

  // Get all subcategories for a given category name (including the category itself)
  const getCategoryAndSubcategories = useCallback(async (categoryName) => {
    try {
      // Handle uncategorized category specially
      if (categoryName === 'Uncategorized') {
        return ['Uncategorized'];
      }
      
      const categories = await fetchCategories();
      
      // Find the category by name
      const targetCategory = categories.find(cat => cat.name === categoryName);
      if (!targetCategory) {
        return [categoryName]; // Fallback to just the category name
      }
      
      // Get all subcategories recursively
      const getAllSubcategories = (parentId) => {
        const directChildren = categories.filter(cat => cat.parent_id === parentId);
        let allChildren = [...directChildren];
        
        directChildren.forEach(child => {
          allChildren = allChildren.concat(getAllSubcategories(child.id));
        });
        
        return allChildren;
      };
      
      // Get all subcategories of the target category
      const subcategories = getAllSubcategories(targetCategory.id);
      
      // Return array of category names (including the root category)
      return [targetCategory.name, ...subcategories.map(cat => cat.name)];
    } catch (err) {
      console.error('Error getting category and subcategories:', err);
      return [categoryName]; // Fallback to just the category name
    }
  }, [fetchCategories]);

  // Get all subcategories for a given category ID (including the category itself)
  const getCategoryAndSubcategoriesById = useCallback(async (categoryId) => {
    try {
      if (!categoryId) {
        return []; // Empty array for "All Categories"
      }
      
      const categories = await fetchCategories();
      
      // Find the category by ID
      const targetCategory = categories.find(cat => cat.id === categoryId);
      if (!targetCategory) {
        return [categoryId]; // Fallback to just the category ID
      }
      
      // Get all subcategories recursively
      const getAllSubcategories = (parentId) => {
        const directChildren = categories.filter(cat => cat.parent_id === parentId);
        let allChildren = [...directChildren];
        
        directChildren.forEach(child => {
          allChildren = allChildren.concat(getAllSubcategories(child.id));
        });
        
        return allChildren;
      };
      
      // Get all subcategories of the target category
      const subcategories = getAllSubcategories(targetCategory.id);
      
      // Return array of category IDs (including the root category)
      return [targetCategory.id, ...subcategories.map(cat => cat.id)];
    } catch (err) {
      console.error('Error getting category and subcategories by ID:', err);
      return [categoryId]; // Fallback to just the category ID
    }
  }, [fetchCategories]);

  // Fetch organizations with fallback to direct table access
  const fetchOrganizations = async () => {
    try {
      console.log('Loading organizations...');
      
      // Try to load from view first, fall back to table if view doesn't exist
      let { data, error } = await supabase
        .from('organization_summary')
        .select('*')
        .order('name');
      
      if (error && error.code === '42P01') {
        // View doesn't exist, try direct table access
        console.log('View not found, trying direct table access');
        const tableResult = await supabase
          .from('organizations')
          .select('*')
          .order('name');
        
        data = tableResult.data;
        error = tableResult.error;
      }
      
      if (error) {
        console.error('Database error:', error);
        throw error;
      }
      
      console.log('Organizations loaded:', data?.length || 0);
      return data || [];
    } catch (err) {
      console.error('Error loading organizations:', err);
      throw err;
    }
  };

  // Fetch users with organization relationships
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          role,
          organization_id,
          location,
          created_at,
          organizations (name)
        `)
        .order('full_name');
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error loading users:', err);
      throw err;
    }
  };

  // Create or update organization
  const saveOrganization = async (formData, selectedOrganization = null) => {
    try {
      console.log('=== ORGANIZATION SAVE ===');
      console.log('Selected Organization:', selectedOrganization);
      console.log('Form Data:', formData);
      
      const orgData = {
        name: formData.name,
        location: formData.location,
        org_type: formData.org_type,
        tier: formData.tier,
        status: formData.status,
        max_users: formData.max_users,
        notes: formData.notes,
        contact_info: JSON.stringify(formData.contact_info),
        pickup_days: formData.pickup_days
      };
      
      console.log('Processed org data:', orgData);

      let result;
      if (selectedOrganization) {
        // Update existing organization
        console.log('Updating organization ID:', selectedOrganization.id);
        result = await supabase
          .from('organizations')
          .update(orgData)
          .eq('id', selectedOrganization.id)
          .select();
      } else {
        // Create new organization
        console.log('Creating new organization');
        result = await supabase
          .from('organizations')
          .insert([orgData])
          .select();
      }
      
      console.log('Supabase result:', result);

      if (result.error) {
        console.error('Supabase error:', result.error);
        throw result.error;
      }

      return result.data[0];
    } catch (err) {
      console.error('Error saving organization:', err);
      throw err;
    }
  };

  // Delete organization
  const deleteOrganization = async (organizationId) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', organizationId);

      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error deleting organization:', err);
      throw err;
    }
  };

  // Fetch detailed organization data (for profile view)
  const fetchOrganizationDetails = async (organizationId) => {
    try {
      // Load organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single();
      
      if (orgError) throw orgError;

      // Load users in this organization
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('organization_id', organizationId)
        .order('full_name');
      
      if (userError) throw userError;

      // Load props owned by this organization
      const { data: propData, error: propError } = await supabase
        .from('props')
        .select('*')
        .eq('owner', orgData.name)
        .order('name')
        .limit(10); // Just recent props for overview
      
      if (propError) console.warn('Error loading props:', propError);

      // Load recent reservations
      const { data: reservationData, error: reservationError } = await supabase
        .from('reservations')
        .select('*')
        .or(`requester_org.eq.${orgData.name},target_org.eq.${orgData.name}`)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (reservationError) console.warn('Error loading reservations:', reservationError);

      return {
        organization: orgData,
        users: userData || [],
        props: propData || [],
        reservations: reservationData || []
      };
    } catch (err) {
      console.error('Error loading organization details:', err);
      throw err;
    }
  };

  // Fetch props with Gufram-specific data for SearchV2
  const fetchPropsForGufram = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎨 Fetching props for Gufram browser...');

      const { data, error } = await supabase
        .from('props')
        .select(`
          id,
          name,
          mask_svg,
          colors,
          usage,
          size,
          position_class,
          sort_order,
          status,
          images,
          main_image_src,
          main_image_shadow,
          owner_org_id,
          owner_org:organizations(name, location),
          category_info:categories!props_category_id_fkey(name)
        `)
        .eq('public_visible', true)
        .order('sort_order');

      if (error) throw error;

      console.log('✅ Fetched props:', data?.length || 0);

      // Base URL for all PropShare hosted images
      const BASE_URL = 'https://propshare.josh-w-martin.com';
      const FALLBACK_IMAGE_SRC = 'https://propshare.josh-w-martin.com/prop-images/AFallback-1000x1000.png';
      const FALLBACK_IMAGE_SHADOW = 'https://propshare.josh-w-martin.com/prop-images/AFallback-Shadow.png';
      const FALLBACK_MASK_SVG = `<?xml version="1.0" encoding="UTF-8"?><svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" width="13.89in" height="13.89in" viewBox="0 0 1000.08 1000.08"><defs><style>.cls-1{fill:#fff;}</style></defs><path class="cls-1" d="M657.72,120.94c1.5-1.34,2.02-4.92,1.52-6.46-1.8-5.59-7.31-4.49-13.13-4.28-4.38.16-7.04,4.73-6.28,8.23s4.19,5.71,8.18,5.71c3.5,0,6.6-.42,9.71-3.2ZM684.49,122.78c1.93-.93,3.72-3.81,3.68-5.33s-1.44-4.57-3.01-5.64c-2.92-1.99-13.84-4.13-16.47,2.38-1.02,2.52.16,6.2,3.25,8.2,3.26,2.1,8.46,2.36,12.56.39h-.01ZM708.91,113.43c-5.16-2.05-10.65-2.41-13.46,3.21-.81,1.63.11,5.5,1.58,6.95,2.3,2.28,14.49,5.86,16.75-.25,1.71-4.62-.37-8.12-4.88-9.91h.01ZM465.15,116.04c-2.33-2.11-7.79-.91-9.5,1.11-1.46,1.73-2.25,6.92-.55,8.93,5.41,6.38,15.96,19.95,23.16,13.56,7.65-6.79-6.09-17.26-13.11-23.61h0ZM531.9,125.65c1.89-2.49.85-7.2-1.11-9.09-1.85-1.78-7.27-2.36-9.3-.37l-12.84,12.54c-2.56,2.5-2.96,7.09-1.18,9.46,7.16,9.5,19.35-5.84,24.43-12.54h0ZM630.54,122.11c.28-1.96-2.27-5.93-4.15-6.44-2.95-.81-7.83-.51-10.91.57-3.48,1.23-5.74,5.17-4.85,7.83,1.3,3.89,4.51,4.99,8.37,5.25,5.01.34,10.85-2.27,11.54-7.21ZM740.73,129.12c2.46-5.57-3.5-8.63-7.67-10.29-4.77-1.9-8.49-.51-10.32,3.64-2.02,4.55,1.17,7.64,5.24,9.59s10.23,2.77,12.75-2.93h0ZM764.84,143.23c1.62-.84,2.94-5.04,2.42-6.54-1.75-5.1-11.02-11.08-17.28-5.73-1.36,1.16-1.83,5.55-1.04,7.16,1.25,2.56,9.81,8.27,15.91,5.11h0ZM368.83,418.65c-2.67.71-5.02,4.61-6.67,6.54-11.86,13.85-20.6,28.75-27.56,45.4-3.98,9.53-7.65,17.97-9.77,28.23l-13.17,9.69-1.82-7.72c-11.33,7.19-20.24,15.15-29.6,24.57l-72.65,73.13c-25.7,25.87-37.22,61.25-35.53,97.54,1.33,28.51,11.96,54.4,29.59,76.5,8.64,10.83,19.55,18,30.22,26.67l18.86,15.31c29.58,24.01,69.98,33.21,107.25,28.41,40.86-5.26,73.58-28.72,98.58-60.38l59.86-75.8c10.32-13.07,17.39-26.69,23.05-42.41,1.94-2.31,5.86-5.42,8.55-7.21,17.45-11.66,30.51-26.56,43.41-42.82l25.61-32.29,37.18-46.73c9.9-12.44,17.08-25.69,24.4-39.78,7.55-5.74,16.6-10.38,23.66-17.42l72.98-72.79c12.27-12.24,22.62-24.26,30.4-39.73,10.35-20.59,15.65-42.18,14.32-65.57-.8-13.98-1-27.32-2.85-41.17-8.68-65.05-58.36-115.46-123.35-123.63-50.06-6.3-98.59,12.63-130.23,52.19l-15.69,19.62-52.92,66.72c-7.8,9.83-13.09,20.26-17.59,31.76-1.72,4.41-4.35,7.42-8.56,9.47-19.43,9.44-35.95,22.56-49.49,39.38l-22.78,28.31c5.2,4.25,9.13,7.82,14.05,10.64,5.59,3.2,11.55,5.25,14.99,11.17,3.12,5.36,6.78,11.39,11.39,15.31,2.19.17,6.26.5,8.17.99-.63,8.24,13.20,23.89,15.82,20.65l18.1-22.34c2.78,4.62,4.39,7.63,6.41,11.65,6.3,12.59,15.86,22.28,25.01,32.83,15.4,17.77,33.11,32.04,56.11,40.42-3.34,6.17-7.24,10.38-11.22,15.45l-22.37,28.53c-.49.62-1.81,1.5-2.42,1.65s-2.14-1.06-2.58-1.84c-19.96-35.38-55.75-61.28-95.55-68.92-1.47-.28-5.17,1.76-5.68.68-1.92-4.45-3.83-9.36-5.95-13.44-2.3-4.42-4.85-8.85-1.29-13.7s5.96-12.40,2.45-18.44c-2.91-5.02-8.22-7.89-13.53-10.36l-3.23-10.20-23.12,4.69c-3.99.81-8.74-.60-13.28.61l.02-.03ZM466,192.15c7.09-6.50,17.58-14.63,13.22-21.68-1.34-2.17-5.50-4.84-8.43-2.71-4.88,3.53-8.75,7.74-13.17,11.89-3.36,3.16-5.35,8.60-1.34,12.25,1.83,1.67,7.88,1.95,9.72.26h0ZM530.48,181.15l-11.82-11.83c-2.97-2.98-7.32-2.77-10.01-.31s-3.17,6.78-.53,9.84c4.87,5.66,16.48,21.14,23.50,12.14,2.04-2.61,1.69-7.00-1.14-9.84h0ZM410.09,355.01l1.52-19,1.81-22.94c.33-4.21.70-8.81,1.49-13.09l14.04-75.93c.81-4.37,1.53-8.29,1.13-12.44l-9.17-1.29c-2.06-3.33-4.76-7.71-8.75-8.23s-8.18.58-12.01,1.04l-14.27,1.70-14.87,1.79c-7.08.85-19,.16-22.81,7.32-1.33,2.49-1.09,6.09-.24,8.88l4.28,14.02,13.58,44.36,24.24,77.85c1.62,5.21,7.72,7.80,11.89,6.57,5.06-1.49,7.71-5.16,8.15-10.60h0ZM263.18,336.55l104.79,55.38c4.77,2.52,10.29.22,12.64-2.70,3.36-4.16,3.62-8.94.08-13.12l-28.31-33.49-53.66-78.21c-1.57-2.28-3.32-4.93-5.29-5.73-2.75,3.22-5.13,5.95-7.81,7.10-7.24-3.99-12.75-1.06-17.19,4.61l-30.69,39.19c-4.26,5.44-1.38,12.68,4.06,15.58l21.39,11.37h0ZM835.90,268.05c-1.24-.99-5.22.58-5.56,2.04-1.57,6.64,1.45,8.67,9.49,15.74,2.25,1.98,6.59,1.04,7.65-1.05,2.78-5.54-6.33-12.51-11.57-16.72h-.01ZM874.52,286.10l9.89-9.78c1.11-1.09.69-4.62-.03-5.78s-3.65-3.10-4.91-2.32c-4.30,2.66-17.06,11.90-12.07,17.02,1.20,1.23,5.73,2.24,7.13.85h-.01ZM187.34,300.17l-8.56-9.04c-1.49-1.58-4.80-2.32-6.41-1.62-1.40.60-4.11,3.93-3.28,5.28,2.96,4.83,12.05,17.12,18.09,11.61,1.03-.94,1.27-5.07.17-6.23h0ZM213.57,307.22c5.27-2.65,15.30-11.93,9.86-17.01-4.68-4.38-12.90,5.80-16.36,9.87-1.08,1.27-.92,4.64-.21,5.93s4.85,2.15,6.71,1.22h0ZM839.12,321.88l7.46-7.57c1.72-1.74,1.96-5.57.45-7.56-4.51-5.95-14,5.96-16.84,9.34-1.33,1.58.12,6.65,1.78,7.50,1.81.94,5.34.13,7.16-1.71h-.01ZM884.61,316.17l-10.04-10.01c-1.34-1.34-5.17-.82-6.58.25-6.31,4.76,6.44,14.15,10.85,17.22,1.21.85,4.31-.26,5.32-1.25s1.48-5.19.45-6.21ZM178.43,343.60l9.18-9.09c1.59-1.58,0-6.64-1.84-7.55-1.60-.79-5.41-.49-6.77.87l-8.72,8.61c-1.78,1.76-1.21,5.86.37,7.38,2.01,1.95,5.82,1.73,7.79-.22h0ZM213.86,326.77c-1.27-1.05-5.16-.39-6.01.80-4.21,5.86,4.15,12.36,9.73,17,1.48,1.23,5.43.43,6.48-.87,4.20-5.22-5.83-13.30-10.20-16.92h0ZM301.26,441.71l53.28-16.68c5.75-1.80,10.03-6.99,8.02-12.53s-7.61-8.45-13.52-8.32c-4.64.10-17.48-1.71-22.56-2.66l-107.67-20.26c-2.79-.53-5.66-1.37-7.95-.76l-.75,13.17c-4.51,1.76-8.24,3.67-9.91,6.46-2.46,4.10-1.08,7.77-.57,12l1.80,15.13,1.83,15.16,1.88,15.65c.39,3.23,2.65,7.16,5.09,8.29,3.32,1.54,6.57,1.51,10.22.37l80.80-25.03h.01ZM581.97,651.92c-1.78-1.64-5.24-1.80-6.58-.64-6.37,5.53,3.95,13.94,8.99,17.45,1.17.81,4.80.33,6.04-.33,6.87-3.71-3.86-12.26-8.45-16.48h0ZM628.96,658.15c1.61-1.91-.29-6.37-2.27-7.14-1.64-.63-4.84-.54-6.53,1.10l-8.73,8.48c-1.40,1.36-1.74,5.79-.19,7.20,5.92,5.39,13.70-4.85,17.72-9.64h0ZM592.76,693.62c.94-1.53-1.95-5.29-3.67-5.57-2.10-.35-5.27.52-6.82,2.01-4.56,4.40-10.78,8.85-8.20,14.50,3.81,8.33,15.92-6.43,18.70-10.93h-.01ZM617.84,688.29c-1.37-1.16-5.89-.15-6.73,1.26s-1.19,5.00-.08,6.35c3.12,3.76,12.91,15.59,17.13,9.68,4.45-6.22-4.10-12.00-10.32-17.30h0Z"/><path class="cls-1" d="M686.82,365.21c-3.50,4.41-6.62,8.15-10.57,11.89-7.21-10.87-13.24-20.46-21.84-29.50-19.42-20.42-42.37-34.33-70.49-43.66l52.99-52.81c24.69-24.61,63.81-26.39,92.57-7.77,9.28,14.41,9.72,26.98,8.42,43.58-1.07,13.75-8.64,24.66-16.87,35.06l-34.22,43.21h.01Z"/><path class="cls-1" d="M788.58,219.54c-44.30-53.98-127.43-62.03-182.30-18.44-7.99,6.34-14.63,13.02-21.91,20.33l-71.19,71.49c-4.45,4.47-5.62,8.01-13.29,9.72l-11.40,2.54c4.76-13.22,12.18-23.04,20.47-33.47l64.03-80.55c4.64-5.84,9.58-10.34,15.01-15.19,32.66-29.16,79.75-39.53,122.19-27.93,38.77,10.60,70.67,38.12,87.07,74.64,1.39,3.09,1.90,5.57,1.11,8.78l-9.78-11.92h0Z"/><path class="cls-1" d="M543.01,391.95c12.41,22.74,33.27,37.79,58.83,40.99,4.47.56,7.01,2.44,8.60,7.13,26.06,4.45,39.93-6.73,56.73-24.79l-.07-9.18,28.06-35.91,40.55-51.47c7.10-9.01,12.23-20.68,12.62-32.01l.77-22.66c8.50,9.47,11.03,21.40,12.54,33.59,3.05,24.59-7.47,46.95-24.80,64.28l-69.93,69.89c-18.40,18.39-43.14,26.53-69.02,20.98-19.66-4.22-37.36-16.37-48.01-33.45-3.08-4.95-5.03-9.87-8.56-14.84-14.62-1.50-29.18,1.96-41.11,10.65-2.93,2.13-5.13,5.32-9.77,4.57,7.50-13.95,20.65-23.23,36.07-27.04,4.72-1.17,10.05-.49,16.49-.72h0Z"/><path class="cls-1" d="M660.19,395.99c-42.84-73.50-134.83-96.35-206.77-50.60-7.42,4.72-14.02,9.67-20.53,15.45-3.61,3.21-5.95,8.44-11.80,8.80,29.54-42.40,82.77-65.45,133.30-59.80,30.96,3.46,59.38,15.46,82.55,36.26,8.05,7.23,15.20,13.98,21.12,23.02,3.59,5.47,8.05,10.58,9.92,17.31.36,3.36-4.17,7.60-7.79,9.56Z"/><path class="cls-1" d="M581.68,521.52l-9.54-2.01c-1.04-1.51,1.17-5.30,2.41-6.73,3.06-3.53,5.49-4.27,9.98-3.32,17.74,3.75,36.59,4.94,54.41.37l21.75-5.58c-9.99,22.53-12.15,17.66-30.16,20.09-9.03,1.22-18.25,1.37-27.17.33l-12.21-1.42c-2.86-.33-6.10-1.03-9.48-1.74h.01Z"/><path class="cls-1" d="M549.21,642.60c-4.06-7.12.61-11.81,5.12-16.34l36.87-36.99c5.11-5.13,9.50-10.05,15.75-14.16-2.64,6.10-6.63,10.35-10.57,15.31l-20.37,25.58c-7.76,9.72-15.93,18.53-26.80,26.60Z"/></svg>`;
      // Helper to resolve image URLs
      const resolveImageUrl = (path) => {
        if (!path) return null;
        // If already a full URL, return as-is
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        // If starts with /, just append to base
        if (path.startsWith('/')) return `${BASE_URL}${path}`;
        // Otherwise prepend base with /
        return `${BASE_URL}/${path}`;
      };

      // Transform data to Gufram format
      const transformedProps = (data || []).map(prop => {
        const maskSvg = prop.mask_svg || FALLBACK_MASK_SVG;
        const resolvedMainImageSrc = resolveImageUrl(prop.main_image_src || FALLBACK_IMAGE_SRC);
        const firstGalleryImage = Array.isArray(prop.images) && prop.images.length > 0
          ? prop.images[0]
          : null;
        const galleryShadowPath =
          firstGalleryImage && typeof firstGalleryImage === 'object'
            ? firstGalleryImage.shadow || firstGalleryImage.shadowSrc || firstGalleryImage.shadow_src
            : null;
        const resolvedMainImageShadow = resolveImageUrl(
          prop.main_image_shadow || galleryShadowPath || FALLBACK_IMAGE_SHADOW
        );
        const colorList = normalizeArrayField(prop.colors);

        return {
          id: prop.id,
          slug: prop.name.toLowerCase().replace(/\s+/g, '-'),
          title: prop.name.toUpperCase(),
          author: prop.owner_org?.name || 'Unknown',
          warehouse: prop.owner_org?.name || 'Unknown',
          location: prop.owner_org?.location || '',
          distance: calculateDistance(prop.owner_org?.location),
          maskSvg,
          placeholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
          images: [{
            src: resolvedMainImageSrc,
            shadowSrc: resolvedMainImageShadow
          }],
          positionClass: prop.position_class || '',
          sortOrder: prop.sort_order || 0,
          colors: colorList,
          dominantColor: colorList[0] || '#000000',
          usage: prop.usage || '',
          size: prop.size || '',
          status: prop.status,
          category: prop.category_info?.name || 'Uncategorized',
          galleryImages: prop.images || [], // Additional detail images for modal
          activeImageIndex: 0,
          // Include org info for cart integration
          orgId: prop.owner_org_id,
          organization: {
            id: prop.owner_org_id,
            name: prop.owner_org?.name || 'Unknown',
            location: prop.owner_org?.location || ''
          }
        };
      });

      console.log('🎨 Transformed props for Gufram:', transformedProps.length);
      console.log('📸 Sample prop:', transformedProps[0]);

      return transformedProps;

    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching props for Gufram:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Debug function to check actual database counts
  const debugCategoryCounts = async () => {
    try {
      console.log('🔍 DEBUG: Checking actual database counts...');
      
      // Get all categories
      const { data: categories, error: catError } = await supabase
        .from('categories')
        .select('id, name, parent_id')
        .order('name');
      
      if (catError) throw catError;
      
      // Get all props with their category assignments
      const { data: props, error: propError } = await supabase
        .from('props')
        .select(`
          id,
          name,
          category_id,
          category_info:categories!props_category_id_fkey(name)
        `)
        .eq('public_visible', true)
        .order('name');
      
      if (propError) throw propError;
      
      console.log('📊 Total categories:', categories.length);
      console.log('📊 Total props:', props.length);
      
      // Check for props without category assignments
      const propsWithoutCategory = props.filter(prop => !prop.category_id || !prop.category_info?.name);
      console.log('❌ Props without category assignment:', propsWithoutCategory.length);
      if (propsWithoutCategory.length > 0) {
        console.log('❌ Uncategorized props:', propsWithoutCategory.map(p => ({ id: p.id, name: p.name, category_id: p.category_id })));
      }
      
      // Check for props with invalid category_id
      const categoryIds = new Set(categories.map(cat => cat.id));
      const propsWithInvalidCategory = props.filter(prop => prop.category_id && !categoryIds.has(prop.category_id));
      console.log('❌ Props with invalid category_id:', propsWithInvalidCategory.length);
      if (propsWithInvalidCategory.length > 0) {
        console.log('❌ Invalid category props:', propsWithInvalidCategory.map(p => ({ id: p.id, name: p.name, category_id: p.category_id })));
      }
      
      // Count props by category name
      const categoryCounts = {};
      let totalCountedProps = 0;
      props.forEach(prop => {
        const categoryName = prop.category_info?.name;
        if (categoryName) {
          categoryCounts[categoryName] = (categoryCounts[categoryName] || 0) + 1;
          totalCountedProps++;
        }
      });
      
      console.log('📊 Props counted in categories:', totalCountedProps);
      console.log('📊 Props not counted:', props.length - totalCountedProps);
      console.log('📊 Direct category counts:', categoryCounts);
      
      // Show hierarchical structure
      const rootCategories = categories.filter(cat => !cat.parent_id);
      console.log('📊 Root categories:', rootCategories.map(cat => cat.name));
      
      return { categories, props, categoryCounts, propsWithoutCategory, propsWithInvalidCategory };
    } catch (err) {
      console.error('Debug error:', err);
    }
  };

  return {
    loading,
    error,
    fetchInitialData,
    fetchPropsForGufram,
    fetchUserLists,
    createUserList,
    addPropToList,
    removePropFromList,
    deleteUserList,
    requestTransfer,
    saveProp,
    deleteProp,
    checkoutProps,
    returnProps,
    submitFeedback,
    submitReservationRequest,
    fetchReservations,
    updateReservationStatus,
    completeReservation,
    // User management functions
    fetchOrganizationUsers,
    addUserToOrganization,
    updateUser,
    removeUserFromOrganization,
    transferUserToOrganization,
    // Organization management functions
    fetchOrganizations,
    fetchUsers,
    saveOrganization,
    deleteOrganization,
    fetchOrganizationDetails,
    // Category management functions
    fetchCategories,
    fetchCategoryTree,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryStats,
    getHierarchicalCategoryStats,
    searchCategories,
    getCategoryAndSubcategories,
    getCategoryAndSubcategoriesById,
    debugCategoryCounts
  };
};

export default useSupabase;
